import type { Coordinate, Entity, StairTravelView } from "./models"
import { stairOpeningBounds } from "./room/ember/stair_geometry"
import { getCurrentTime } from "./time_util"
import { directionFromRaw } from "./util"

export const STAIR_TRANSITION_MS = 450
export const STAIR_WALK_SPEED = 80
// Upright artwork: feet at row 92, top beam at row 16.
export const STAIR_LADDER_HEIGHT = 76
export const STAIR_CLIMB_PX = 32
export const STAIR_UP_TRANSITION_MS = STAIR_CLIMB_PX / STAIR_WALK_SPEED * 1000

/** The same rung height is used by both floor projections, including at a room handoff. */
export const stairFlightHeight = (view: StairTravelView): number => {
    const elapsed = view.climb?.elapsedMs ?? view.durationMs - view.remainingMs
    const progress = Math.max(0, Math.min(1, (elapsed - (view.phase === "depart" ? view.walkMs : 0)) / Math.max(1, view.transitionMs)))
    return view.flight.from + (view.flight.to - view.flight.from) * progress
}

/** A view only: this never moves or duplicates the authoritative entity. */
export const stairTravelInRoom = (view: StairTravelView | undefined, roomId: string): StairTravelView | undefined => {
    const flight = view?.flight
    if (!flight?.surface || flight.continuous || ![flight.lowerRoomId, flight.upperRoomId].includes(roomId)) return undefined
    return { ...view, roomId, projection: true }
}

/** Walking stays in floor coordinates; the sink/rise is a separate body-only effect. */
export const stairTravelPose = (view: StairTravelView, elapsedMs: number) => {
    const elapsed = Math.max(0, Math.min(view.durationMs, view.climb?.elapsedMs ?? elapsedMs))
    const departing = view.phase === "depart"
    const walkElapsed = departing ? elapsed : elapsed - view.transitionMs
    const walking = (!view.climb || view.climb.moving) && view.walkMs > 0 && (departing ? elapsed < view.walkMs : elapsed >= view.transitionMs && elapsed < view.durationMs)
    const walkProgress = view.walkMs ? Math.max(0, Math.min(1, walkElapsed / view.walkMs)) : 1
    const transition = Math.max(0, Math.min(1, (departing ? elapsed - view.walkMs : elapsed) / view.transitionMs))
    const smooth = view.climb ? transition : transition * transition * (3 - 2 * transition)
    const depth = departing ? smooth : 1 - smooth
    const below = departing ? view.direction === "down" : view.direction === "up"
    const transitioning = departing ? elapsed >= view.walkMs : elapsed < view.transitionMs
    const ramp = view.ramp
    const upTile = departing ? view.direction === "up" : view.direction === "down"
    if (view.flight) {
        const flight = view.flight
        const height = stairFlightHeight({ ...view, remainingMs: view.durationMs - elapsed })
        const upper = view.roomId === flight.upperRoomId
        const atEnd = !view.projection && !departing && elapsed >= view.durationMs
        // Crossing the upstairs lip already means gripping the ladder, even
        // while the player is still aligning with the shaft before descending.
        const climbing = transitioning || departing && upper && view.direction === "down"
        const location = atEnd ? view.end : transitioning ? flight.base :
            { x: view.start.x + (view.end.x - view.start.x) * walkProgress,
                y: view.start.y + (view.end.y - view.start.y) * walkProgress }
        const bodyOffset = atEnd || !transitioning ? 0 : (upper ? flight.rise : 0) - height
        // Include the entry alignment in the animation's travel distance so the
        // arms move with up/down input there too, then join the vertical cycle.
        const entryDistance = departing && !transitioning && climbing
            ? Math.hypot(view.end.x - view.start.x, view.end.y - view.start.y) * (1 - walkProgress) : 0
        // Map the upper hatch rim to the top of the lower ladder. Building floor
        // spacing is compressed for perspective and does not measure ladder height.
        const edge = stairOpeningBounds({ x: Math.floor(flight.base.x / 32), y: Math.floor(flight.base.y / 32) }).south - (upper ? 0 : flight.rise)
        return { location, bodyOffset, walking: walking || transitioning && elapsed < view.durationMs && (!view.climb || view.climb.moving),
            transitioning, ladderRise: climbing && !atEnd ? height + entryDistance : undefined,
            below: upper, entranceOffset: 0, headClip: undefined,
            split: climbing && !atEnd && flight.surface && !flight.continuous ? { edge, above: upper } : undefined,
            direction: view.climb?.direction ?? (view.direction === "up" ? "Up" as const : "Down" as const),
            angle: (view.climb?.direction ?? (view.direction === "up" ? "Up" : "Down")) === "Up" ? -Math.PI / 2 : Math.PI / 2 }
    }
    // Upright stairs keep floor coordinates at the base while the body climbs.
    // Keep the climbing body visible in either direction through the room handoff.
    if (ramp && upTile) {
        const transitionElapsed = Math.max(0, Math.min(view.transitionMs, departing ? elapsed - view.walkMs : elapsed))
        const progress = Math.min(1, transitionElapsed / Math.max(1, view.transitionMs))
        const climb = departing ? progress : 1 - progress
        const start = departing ? view.start : ramp.entry
        const end = departing ? ramp.entry : view.end
        const climbing = transitioning && elapsed < view.durationMs
        return { location: transitioning ? { ...ramp.entry }
            : { x: start.x + (end.x - start.x) * walkProgress, y: start.y + (end.y - start.y) * walkProgress },
            walking: walking || climbing && (!view.climb || view.climb.moving), transitioning, bodyOffset: -STAIR_CLIMB_PX * climb,
            // How far up the ladder the body is while on it: a body that draws a climb times it by this.
            ladderRise: transitioning ? STAIR_CLIMB_PX * climb : undefined,
            below, entranceOffset: 0, headClip: undefined,
            direction: view.climb?.direction ?? (transitioning ? departing ? "Up" as const : "Down" as const : view.exitDirection),
            angle: transitioning ? (view.climb?.direction === "Up" || !view.climb?.direction && departing ? -Math.PI / 2 : Math.PI / 2) : view.exitAngle }
    }
    // A floor opening uses the same fixed-ground rise on arrival and sink on
    // departure. Finish walking to its center before moving the body vertically.
    if (ramp && !upTile) {
        const center = departing ? view.end : view.start
        const start = departing ? view.start : center
        const end = departing ? center : view.end
        const entranceOffset = view.climb ? 0 : 12 * (transitioning ? 1 : departing ? walkProgress : 1 - walkProgress)
        return { location: transitioning ? { ...center }
            : { x: start.x + (end.x - start.x) * walkProgress, y: start.y + (end.y - start.y) * walkProgress },
            walking, transitioning, bodyOffset: 64 * depth - entranceOffset, below: true, entranceOffset,
            // Keep hands and feet on the rungs while passing through the floor opening too.
            ladderRise: transitioning ? -64 * depth : undefined,
            headClip: undefined, direction: view.climb?.direction ?? view.exitDirection,
            angle: view.climb?.direction ? view.climb.direction === "Up" ? -Math.PI / 2 : Math.PI / 2 : view.exitAngle }
    }
    const approachEnd = ramp ? (upTile ? ramp.entry : ramp.top) : view.end
    const exitStart = ramp ? (upTile ? ramp.entry : ramp.top) : view.start
    const start = !departing ? exitStart : view.start
    const end = departing ? approachEnd : view.end
    const departureHeight = view.direction === "down" ? 8 : 16
    const entranceOffset = departing && (ramp || view.direction === "down") ? departureHeight * walkProgress
        : ramp ? 16 * (transitioning ? 1 : 1 - walkProgress) : 0
    const from = ramp ? (departing === upTile ? ramp.entry : ramp.top) : view.start
    const to = ramp ? (departing === upTile ? ramp.top : ramp.entry) : view.end
    const location = ramp && transitioning
        ? { x: from.x + (to.x - from.x) * transition,
            y: from.y + (to.y - from.y) * transition }
        : { x: start.x + (end.x - start.x) * walkProgress,
            y: start.y + (end.y - start.y) * walkProgress }
    return { location, walking: walking || !!ramp && transitioning && elapsed < view.durationMs,
        // Downstairs departure starts 8px south of the usual 16px raised entrance.
        transitioning, bodyOffset: (ramp && upTile ? -24 * depth : (below ? 1 : -1) * 64 * depth) - entranceOffset, below, entranceOffset,
        headClip: ramp && upTile && transitioning ? depth : undefined,
        direction: ramp && transitioning ? directionFromRaw(from.x, from.y, to.x, to.y) ?? view.exitDirection : view.exitDirection,
        angle: ramp && transitioning ? Math.atan2(to.y - from.y, to.x - from.x) : view.exitAngle }
}

/** Retire all pending phases together so an interrupted climb cannot resume later. */
export const clearStairTravel = (entity: Entity): void => {
    if (entity.stairTravel || entity.stairTravelArrival || entity.stairTravelDestination) entity.lastUpdateTs = getCurrentTime()
    entity.stairTravel = undefined
    entity.stairTravelUntilTs = undefined
    entity.stairTravelDestination = undefined
    entity.stairTravelArrival = undefined
}

/** Leave the ladder at its current height, including when its supporting tiles disappear. */
export const dismountStairClimb = (entity: Entity): void => {
    const view = entity.stairTravel
    if (!view) return
    const pose = stairTravelPose(view, 0)
    const upright = (view.phase === "depart") === (view.direction === "up")
    const roomId = view.flight?.lowerRoomId ?? (!upright && view.lowerLanding ? view.lowerLanding.roomId : entity.roomId)
    const height = view.flight ? stairFlightHeight(view) : Math.max(0, upright ? -pose.bodyOffset : 64 - pose.bodyOffset)
    if (height > 0) {
        const gravity = .0015
        const durationMs = Math.sqrt(2 * height / gravity)
        entity.floorFall = { id: `${view.id}:jump`, roomId, stairJump: true, fromAbove: true,
            phase: "arriving", durationMs, remainingMs: durationMs, distance: height, levelsFallen: 0,
            trajectory: { gravity, distanceBefore: 0, endElapsedMs: durationMs, totalDistance: height } }
        entity.floorFallUntilTs = getCurrentTime() + durationMs
        entity.floorFallRoomId = roomId
    }
    clearStairTravel(entity)
}

/** Shared by local prediction and authoritative input processing. */
export const advanceStairClimb = (entity: Entity, input: Coordinate | undefined, seconds: number): boolean => {
    const view = entity.stairTravel
    if (!view?.climb) return false
    // Ignore room-refresh nudges; intentional sideways input leaves the ladder.
    if (Math.abs(input?.x ?? 0) > 1) {
        dismountStairClimb(entity)
        return false
    }
    const vertical = Math.sign(input?.y || 0)
    const forward = view.direction === "up" ? -vertical : vertical
    const dt = Math.max(0, Math.min(.1, Number.isFinite(seconds) ? seconds : 0))
    const speed = Number.isFinite(input?.y) ? Math.abs(input.y) : 0
    const previous = view.climb.elapsedMs
    // Convert the timeline to travelled pixels so stairs use the same input speed as the floor.
    // Upright stairs span 32px; the passage through the opening spans 64px.
    const walkPx = view.walkMs > 0 ? Math.hypot(view.end.x - view.start.x, view.end.y - view.start.y) : 0
    const climbPx = view.flight ? Math.abs(view.flight.to - view.flight.from)
        : (view.phase === "depart") === (view.direction === "up") ? STAIR_CLIMB_PX : 64
    const firstMs = view.phase === "depart" ? view.walkMs : view.transitionMs
    const firstPx = view.phase === "depart" ? walkPx : climbPx
    const lastMs = view.phase === "depart" ? view.transitionMs : view.walkMs
    const lastPx = view.phase === "depart" ? climbPx : walkPx
    const distance = previous < firstMs && firstMs > 0 ? previous / firstMs * firstPx
        : firstPx + (lastMs > 0 ? (previous - firstMs) / lastMs * lastPx : 0)
    const next = Math.max(0, Math.min(firstPx + lastPx, distance + forward * speed * dt))
    view.climb.elapsedMs = next <= 0 ? 0 : next >= firstPx + lastPx ? view.durationMs
        : next < firstPx && firstPx > 0 ? next / firstPx * firstMs
        : firstMs + (lastPx > 0 ? (next - firstPx) / lastPx * lastMs : 0)
    view.climb.moving = previous !== view.climb.elapsedMs
    if (vertical) view.climb.direction = vertical < 0 ? "Up" : "Down"
    const pose = stairTravelPose(view, 0)
    Object.assign(entity.location, pose.location)
    entity.movement.moving = pose.walking
    entity.movement.direction = vertical < 0 ? "Up" : vertical > 0 ? "Down" : pose.direction
    entity.movement.angle = vertical < 0 ? -Math.PI / 2 : Math.PI / 2
    entity.speed2 = { x: 0, y: 0 }
    if (forward < 0 && view.climb.elapsedMs === 0) {
        if (view.phase === "depart") clearStairTravel(entity)
        else view.climb.reverse = true
    }
    return true
}
